import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { 
  ArrowLeft, ArrowRight, GraduationCap, CheckCircle, Loader2, 
  Clock, Calendar, BookOpen, Users, Award, AlertCircle, Linkedin,
  Mail, Phone, Briefcase, Star
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MentorRegistrationPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, token } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [existingRegistration, setExistingRegistration] = useState(null);
  
  const [formData, setFormData] = useState({
    name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    linkedin_url: user?.linkedin_url || '',
    expertise: '',
    years_experience: '',
    current_role: '',
    company: user?.company_name || '',
    bio: '',
    motivation: '',
    availability: '',
    preferred_startups: [],
    masterclass_topics: [],
    photo_url: '',
    referred_by: ''
  });

  // Auto-fill referral code from ?ref=XYZ
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || '').trim().toUpperCase();
    if (ref) {
      setFormData(prev => ({ ...prev, referred_by: ref }));
    }
  }, []);

  const [photoUploading, setPhotoUploading] = useState(false);
  const handlePhotoUpload = async (file) => {
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      toast({ title: 'Invalid file', description: 'Please upload PNG, JPG, or WebP.', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Too large', description: 'Photo must be under 5 MB.', variant: 'destructive' });
      return;
    }
    setPhotoUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData(prev => ({ ...prev, photo_url: `${BACKEND_URL}${res.data.url}` }));
      toast({ title: 'Photo uploaded', description: 'Looking good.' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setPhotoUploading(false);
    }
  };

  const expertiseAreas = [
    "Governance & Compliance",
    "Financial Management",
    "Legal & Regulatory",
    "Technology & Product",
    "Marketing & Sales",
    "Operations",
    "Fundraising & Investment",
    "Leadership & HR"
  ];

  const masterclassTopics = [
    "Week 1-2: Governance Foundations",
    "Week 3-4: Compliance & Risk",
    "Week 5-6: Board Structure & Financial Controls"
  ];

  const startupTypes = ["Climate Tech", "FinTech / RegTech", "Cross-Border Services", "All Types"];

  useEffect(() => {
    if (user?.email) {
      setFormData(prev => ({
        ...prev,
        name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
        linkedin_url: user.linkedin_url || '',
        company: user.company_name || ''
      }));
      checkExistingRegistration();
    }
  }, [user]);

  const checkExistingRegistration = async () => {
    try {
      const response = await axios.get(`${API}/mentor-registrations/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setExistingRegistration(response.data);
      }
    } catch (error) {
      // No existing registration
    }
  };

  const handleTopicToggle = (topic) => {
    setFormData(prev => ({
      ...prev,
      masterclass_topics: prev.masterclass_topics.includes(topic)
        ? prev.masterclass_topics.filter(t => t !== topic)
        : [...prev.masterclass_topics, topic]
    }));
  };

  const handleStartupToggle = (startup) => {
    setFormData(prev => ({
      ...prev,
      preferred_startups: prev.preferred_startups.includes(startup)
        ? prev.preferred_startups.filter(s => s !== startup)
        : [...prev.preferred_startups, startup]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.expertise) {
      toast({ title: "Required fields missing", description: "Please fill all required fields", variant: "destructive" });
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/mentor-registrations`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setSubmitted(true);
      toast({ title: "Registration Submitted!", description: "Pending admin approval" });
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Please try again", variant: "destructive" });
    }
    setLoading(false);
  };

  // Show existing registration status
  if (existingRegistration) {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      approved: 'bg-green-100 text-green-800 border-green-300',
      rejected: 'bg-red-100 text-red-800 border-red-300'
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <Header />
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl ${
              existingRegistration.status === 'approved' ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
              existingRegistration.status === 'pending' ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
              'bg-gradient-to-r from-red-500 to-pink-500'
            }`}>
              {existingRegistration.status === 'approved' ? <CheckCircle className="h-12 w-12 text-white" /> :
               existingRegistration.status === 'pending' ? <Clock className="h-12 w-12 text-white" /> :
               <AlertCircle className="h-12 w-12 text-white" />}
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {existingRegistration.status === 'approved' ? 'You\'re Approved!' :
               existingRegistration.status === 'pending' ? 'Registration Pending' :
               'Registration Status'}
            </h1>
            
            <Badge className={`${statusColors[existingRegistration.status]} text-lg px-4 py-2 mb-6`}>
              {existingRegistration.status.toUpperCase()}
            </Badge>
            
            <Card className="text-left mb-8">
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Your Registration Details:</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Expertise:</strong> {existingRegistration.expertise}</p>
                  <p><strong>Experience:</strong> {existingRegistration.years_experience} years</p>
                  <p><strong>Topics:</strong> {existingRegistration.masterclass_topics?.join(', ') || 'All'}</p>
                </div>
                
                {existingRegistration.status === 'approved' && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">🎉 Next Steps:</h4>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• Access the Masterclass materials</li>
                      <li>• Connect with assigned startups</li>
                      <li>• Schedule mentoring sessions</li>
                    </ul>
                  </div>
                )}
                
                {existingRegistration.status === 'pending' && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <h4 className="font-semibold text-yellow-800 mb-2">⏳ What's Next:</h4>
                    <p className="text-yellow-700 text-sm">
                      Our admin team is reviewing your application. You'll receive an email once approved.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/mentors')} variant="outline">
                View All Mentors
              </Button>
              <Button onClick={() => navigate('/founders')} className="bg-gradient-to-r from-orange-500 to-amber-500">
                Browse Founders
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
        <Header />
        <div className="container mx-auto px-6 py-16">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-24 h-24 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-8 shadow-2xl">
              <CheckCircle className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Registration Submitted!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for registering as a Masterclass Mentor. Your application is now pending admin approval.
            </p>
            <Card className="bg-orange-50 border-orange-200 mb-8">
              <CardContent className="p-6">
                <h3 className="font-semibold text-orange-800 mb-3">What happens next?</h3>
                <ul className="text-left text-orange-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <Clock className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>Admin will review your application within 48 hours</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>You'll receive an email notification upon approval</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <BookOpen className="h-5 w-5 mt-0.5 flex-shrink-0" />
                    <span>Once approved, access Masterclass materials and connect with startups</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate('/mentors')} className="bg-gradient-to-r from-orange-500 to-amber-500">
                View Mentors
              </Button>
              <Button onClick={() => navigate('/')} variant="outline">
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white py-12">
        <div className="container mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <GraduationCap className="h-10 w-10" />
            <h1 className="text-4xl font-bold">Masterclass Mentor Registration</h1>
          </div>
          <p className="text-xl opacity-90 max-w-2xl mx-auto">
            Join our 6-week Masterclass program as a mentor. Guide startups through governance, compliance, and growth.
          </p>
        </div>
      </div>

      {/* Program Info */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-orange-200">
            <CardContent className="p-4 text-center">
              <Calendar className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">6 Weeks</p>
              <p className="text-sm text-gray-500">Program Duration</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">45 Startups</p>
              <p className="text-sm text-gray-500">In Portfolio</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4 text-center">
              <BookOpen className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">3 Modules</p>
              <p className="text-sm text-gray-500">Curriculum</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4 text-center">
              <Award className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <p className="font-semibold text-gray-900">Certificate</p>
              <p className="text-sm text-gray-500">Upon Completion</p>
            </CardContent>
          </Card>
        </div>

        {/* Registration Form */}
        <Card className="max-w-3xl mx-auto shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Star className="h-6 w-6 text-orange-500" />
              Mentor Application
            </CardTitle>
            <CardDescription>
              Complete your application to become a Masterclass Mentor. Approval required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              
              {/* Personal Info */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>

                {/* Profile photo upload — top of form */}
                <div className="flex items-center gap-5 mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="relative">
                    <div
                      className="w-24 h-24 rounded-full bg-white border-2 border-amber-400 overflow-hidden flex items-center justify-center"
                      data-testid="mentor-photo-preview"
                    >
                      {formData.photo_url ? (
                        <img src={formData.photo_url} alt="Mentor preview" className="w-full h-full object-cover" />
                      ) : (
                        <Award className="w-9 h-9 text-amber-400" />
                      )}
                    </div>
                    {formData.photo_url && (
                      <div className="absolute -bottom-1 -right-1 bg-amber-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded tracking-widest">
                        FACULTY
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-gray-900">Profile photo</Label>
                    <p className="text-xs text-gray-600 mt-0.5">Appears on your public mentor profile and Faculty badge. PNG/JPG/WebP · under 5 MB.</p>
                    <label
                      htmlFor="mentor-photo-input"
                      className="inline-flex items-center mt-2 px-4 py-2 bg-amber-500 text-white text-xs font-bold tracking-widest uppercase rounded cursor-pointer hover:bg-amber-600 transition"
                      data-testid="mentor-photo-upload-btn"
                    >
                      {photoUploading ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : null}
                      {photoUploading ? "Uploading…" : formData.photo_url ? "Replace photo" : "Upload photo"}
                    </label>
                    <input
                      id="mentor-photo-input"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      className="hidden"
                      onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input 
                      value={formData.name} 
                      onChange={(e) => setFormData({...formData, name: e.target.value})} 
                      placeholder="Your full name" 
                      required 
                      data-testid="mentor-name"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input 
                        type="email" 
                        value={formData.email} 
                        onChange={(e) => setFormData({...formData, email: e.target.value})} 
                        className="pl-10" 
                        placeholder="you@example.com" 
                        required 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input 
                        value={formData.phone} 
                        onChange={(e) => setFormData({...formData, phone: e.target.value})} 
                        className="pl-10" 
                        placeholder="+971 50 123 4567" 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>LinkedIn Profile *</Label>
                    <div className="relative">
                      <Linkedin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input 
                        value={formData.linkedin_url} 
                        onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})} 
                        className="pl-10" 
                        placeholder="linkedin.com/in/..." 
                        required
                      />
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="flex items-center gap-2">
                      Referral code <span className="text-xs text-amber-600 tracking-widest font-bold">OPTIONAL</span>
                    </Label>
                    <Input
                      value={formData.referred_by}
                      onChange={(e) => setFormData({ ...formData, referred_by: e.target.value.toUpperCase() })}
                      placeholder="e.g. SHIRA1B2C — paste the code your referrer shared with you"
                      className="font-mono tracking-widest uppercase"
                      maxLength={12}
                      data-testid="mentor-referral-code"
                    />
                    <p className="text-xs text-gray-500 mt-1">If a current SKI Axis mentor invited you, paste their code here. They earn 5% of your first-year workshop revenue.</p>
                  </div>
                </div>
              </div>

              {/* Professional Info */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Background</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Area of Expertise *</Label>
                    <Select value={formData.expertise} onValueChange={(v) => setFormData({...formData, expertise: v})}>
                      <SelectTrigger data-testid="expertise"><SelectValue placeholder="Select expertise" /></SelectTrigger>
                      <SelectContent>
                        {expertiseAreas.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Years of Experience *</Label>
                    <Select value={formData.years_experience} onValueChange={(v) => setFormData({...formData, years_experience: v})}>
                      <SelectTrigger><SelectValue placeholder="Select experience" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5-10">5-10 years</SelectItem>
                        <SelectItem value="10-15">10-15 years</SelectItem>
                        <SelectItem value="15-20">15-20 years</SelectItem>
                        <SelectItem value="20+">20+ years</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Current Role</Label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input 
                        value={formData.current_role} 
                        onChange={(e) => setFormData({...formData, current_role: e.target.value})} 
                        className="pl-10" 
                        placeholder="CEO, Advisor, Consultant..." 
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Company/Organization</Label>
                    <Input 
                      value={formData.company} 
                      onChange={(e) => setFormData({...formData, company: e.target.value})} 
                      placeholder="Your company" 
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Professional Bio</Label>
                    <Textarea 
                      value={formData.bio} 
                      onChange={(e) => setFormData({...formData, bio: e.target.value})} 
                      placeholder="Brief professional background and achievements..." 
                      className="min-h-20"
                    />
                  </div>
                </div>
              </div>

              {/* Masterclass Preferences */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Masterclass Preferences</h3>
                
                <div className="mb-4">
                  <Label className="mb-2 block">Which modules would you like to teach? *</Label>
                  <div className="space-y-2">
                    {masterclassTopics.map(topic => (
                      <div 
                        key={topic} 
                        onClick={() => handleTopicToggle(topic)}
                        className={`p-3 rounded-lg cursor-pointer transition-all border ${
                          formData.masterclass_topics.includes(topic) 
                            ? 'bg-orange-100 border-orange-400 text-orange-800' 
                            : 'bg-white border-gray-200 hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                            formData.masterclass_topics.includes(topic) ? 'bg-orange-500 border-orange-500' : 'border-gray-300'
                          }`}>
                            {formData.masterclass_topics.includes(topic) && <CheckCircle className="h-3 w-3 text-white" />}
                          </div>
                          <span className="font-medium">{topic}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <Label className="mb-2 block">Preferred Startup Types</Label>
                  <div className="flex flex-wrap gap-2">
                    {startupTypes.map(startup => (
                      <div 
                        key={startup} 
                        onClick={() => handleStartupToggle(startup)}
                        className={`px-4 py-2 rounded-full cursor-pointer transition-all ${
                          formData.preferred_startups.includes(startup) 
                            ? 'bg-orange-500 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-orange-100'
                        }`}
                      >
                        {startup}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Weekly Availability</Label>
                  <Select value={formData.availability} onValueChange={(v) => setFormData({...formData, availability: v})}>
                    <SelectTrigger><SelectValue placeholder="Select availability" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2-4 hours">2-4 hours/week</SelectItem>
                      <SelectItem value="4-8 hours">4-8 hours/week</SelectItem>
                      <SelectItem value="8+ hours">8+ hours/week</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Motivation */}
              <div>
                <Label>Why do you want to be a mentor? *</Label>
                <Textarea 
                  value={formData.motivation} 
                  onChange={(e) => setFormData({...formData, motivation: e.target.value})} 
                  placeholder="Share your motivation for joining the Masterclass program..." 
                  className="min-h-24"
                  required
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full py-6 text-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 shadow-xl"
                data-testid="submit-mentor-registration"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <GraduationCap className="h-5 w-5 mr-2" />}
                Submit Registration (Pending Approval)
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MentorRegistrationPage;
